package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacterToCreate
import fr.manyverse.app.domain.model.CharacterToUpdate
import fr.manyverse.app.domain.repository.CharacterRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Count
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

private const val CHARACTERS_TABLE = "characters"

class SupabaseCharacterRepository(
    private val supabaseClient: SupabaseClient,
) : CharacterRepository {

    override suspend fun getCharactersByUserId(
        universeId: String,
        userId: String
    ): Result<List<Character>> {
        return try {
            val characters = supabaseClient
                .from(CHARACTERS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("universe_id", universeId)
                        eq("user_id", userId)
                    }
                }
                .decodeList<Character>()

            Result.success(characters)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun getAllCharactersByUserId(
        userId: String
    ): Result<List<Character>> {
        return try {
            val characters = supabaseClient
                .from(CHARACTERS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("user_id", userId)
                    }
                }
                .decodeList<Character>()

            Result.success(characters)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun countCharactersByUserId(userId: String): Result<Int> {
        return try {
            val result = supabaseClient
                .from(CHARACTERS_TABLE)
                .select {
                    count(Count.EXACT)
                    filter { eq("user_id", userId) }
                }
            Result.success((result.countOrNull() ?: 0L).toInt())
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun countCharactersInUniverse(
        universeId: String,
        userId: String
    ): Result<Int> {
        return try {
            val result = supabaseClient
                .from(CHARACTERS_TABLE)
                .select {
                    count(Count.EXACT)
                    filter {
                        eq("universe_id", universeId)
                        eq("user_id", userId)
                    }
                }
            Result.success((result.countOrNull() ?: 0L).toInt())
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun createCharacter(
        universeId: String,
        character: CharacterToCreate
    ): Result<Character> {
        return try {
            val insertDto = CharacterInsertDto(
                universeId = universeId,
                userId = character.userId,
                name = character.name,
                story = character.story
            )

            val createdCharacter = supabaseClient
                .from(CHARACTERS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<Character>()

            Result.success(createdCharacter)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun updateCharacter(
        characterId: String,
        character: CharacterToUpdate
    ): Result<Character> {
        return try {
            val updatedCharacter = supabaseClient
                .from(CHARACTERS_TABLE)
                .update(character) {
                    filter {
                        eq("id", characterId)
                    }
                    select()
                }
                .decodeSingle<Character>()

            Result.success(updatedCharacter)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun updateCharacterSheet(
        characterId: String,
        sheet: CharacterSheet
    ): Result<Character> {
        return try {
            val updateDto = CharacterSheetUpdateDto(sheet = sheet)

            val updatedCharacter = supabaseClient
                .from(CHARACTERS_TABLE)
                .update(updateDto) {
                    filter {
                        eq("id", characterId)
                    }
                    select()
                }
                .decodeSingle<Character>()

            Result.success(updatedCharacter)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun deleteCharacter(
        characterId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(CHARACTERS_TABLE)
                .delete {
                    filter {
                        eq("id", characterId)
                    }
                }
            return Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun getCharacterById(
        characterId: String
    ): Result<Character?> {
        return try {
            val character = supabaseClient
                .from(CHARACTERS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("id", characterId)
                    }
                }
                .decodeSingleOrNull<Character>()

            Result.success(character)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    @Serializable
    private data class CharacterInsertDto(
        @SerialName("universe_id")
        val universeId: String,

        @SerialName("user_id")
        val userId: String,

        @SerialName("name")
        val name: String,

        @SerialName("story")
        val story: String
    )

    @Serializable
    private data class CharacterSheetUpdateDto(
        @SerialName("sheet")
        val sheet: CharacterSheet
    )
}
